import { useMemo, useRef, useState } from "react"

import { Alert, Col, Divider, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { useLocation } from "react-router-dom"

import FAQModal from "../../Components/Modal/FAQModal"
import TitleSection from "../../Components/TitleSection/TitleSection"
import TourComponent from "../../Components/Tour"
import VideoPlayerModal from "../../Components/VideoPlayerModal/VideoPlayerModal"
import UserActions from "../../Containers/User/reducer"
import Icon from "../../Icon"
import { onlyOwnerAccess } from "../../Utils/permission"
import { useShopifyOnlyUser } from "../Balance/hooks/useShopifyOnlyUser"
import AutomaticTopUpBalance from "./components/AutomaticTopUpBalance"
import { balanceFAQ } from "./components/BalanceFAQList"
import CurrentBalance from "./components/CurrentBalance"
import PaymentMethods from "./components/PaymentMethods"
import { usePageQuery } from "./usePageQuery"

const { Text, Title, Link } = Typography

const Balance = ({ billingTabRef, withTitleBlock }) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { user_id } = usePageQuery(search)
  const demoTourStep = useSelector((state) => state.user.demoTourStep)
  const balanceEnabled = useSelector((state) => state.auth.userInfo?.["balance_feature"])
  const userInfoForAdmin = useSelector((state) => state.user.userInfoForAdmin)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const balance = useSelector((state) => state?.balance?.balance)
  const isAdmin = useSelector((state) => state.auth.isAdmin)
  const businessAccountRole = useSelector((state) => state.auth.userInfo?.ba_role?.id)
  const { isShopifyOnlyUser } = useShopifyOnlyUser()
  const addFundsRef = useRef(null)
  const scaleRef = useRef(null)
  const paymentMethodsRef = useRef(null)
  const topUpRef = useRef(null)
  const [openFAQ, setOpenFaq] = useState(false)
  const [isTutorialVisible, setTutorialVisible] = useState(false)
  const isAdminViewingUser = isAdmin && !!user_id
  const showAccountBalanceNotice = !isAdmin && [2, 3, 4].includes(Number(businessAccountRole))
  const ownerFullName = [businessAccount?.owner?.first_name, businessAccount?.owner?.last_name]
    .filter(Boolean)
    .join(" ")
  const adminFullName = [userInfoForAdmin?.first_name, userInfoForAdmin?.last_name]
    .filter(Boolean)
    .join(" ")
  const displayFullName = isAdminViewingUser ? adminFullName : ownerFullName
  const isBaOwner = onlyOwnerAccess.includes(Number(businessAccountRole))
  // The BA-owner title reduction only applies to the owner viewing their own pages; an
  // admin looking up a user always sees the full title, regardless of that user's role.
  const showFullTitle = isAdminViewingUser || !isBaOwner

  const balanceTitle = !showFullTitle
    ? "Balance & Payment methods"
    : businessAccount?.name
      ? displayFullName
        ? `Balance & Payment methods for ${businessAccount.name} (${displayFullName})`
        : `Balance & Payment methods for ${businessAccount.name}`
      : displayFullName
        ? `Balance & Payment methods for ${displayFullName}`
        : "Balance & Payment methods"

  if (!balanceEnabled && !isAdmin) navigate("/settings/billing")

  const steps = useMemo(
    () => [
      {
        title: (
          <Text type="secondary">
            This is your go-to button to quickly add money to your Chargeback account. Just click it
            to start the top-up process.
          </Text>
        ),
        target: () => addFundsRef?.current
      },
      {
        title: (
          <Text type="secondary">
            Set your safety net here! Tell us the minimum balance you're comfortable with. If your
            balance dips below this number, our auto-top-up will kick in.
          </Text>
        ),
        target: () => topUpRef?.current,
        placement: "top"
      },
      {
        title: (
          <Text type="secondary">
            This shows your current payment method. You can see which card is set as default, and
            its last four digits, so you know which one we'll use for charges or auto-top-ups.
          </Text>
        ),
        target: () => paymentMethodsRef?.current
      },
      {
        title: (
          <Text type="secondary">
            Keep the balance on the recommended level and don’t let it go below the minimum. The
            levels depend on your activity.
          </Text>
        ),
        target: () => scaleRef?.current
      },
      {
        title: (
          <Text type="secondary">
            This is where you store all the information related to your payments.
          </Text>
        ),
        target: () => billingTabRef?.current,
        nextButtonProps: {
          onClick: () => {
            navigate("/settings/billing")
            dispatch(UserActions.setDemoTourStep(8))
          },
          children: <text>Next</text>
        }
      }
    ],
    [addFundsRef, billingTabRef, scaleRef, paymentMethodsRef, topUpRef]
  )

  return (
    <div style={{ padding: isMobile ? 16 : 32, paddingTop: isMobile ? 16 : 32 }}>
      {withTitleBlock && (
        <>
          <FAQModal
            open={openFAQ}
            close={() => setOpenFaq(false)}
            data={balanceFAQ}
            title={
              <Typography.Text strong>
                Learn how your balance works and why it changes.
                <br />
                Want more details?{" "}
                <Typography.Link>
                  <a
                    href="https://help.chargeback.io/en/articles/12782877-what-is-balance-billing"
                    target="_blank"
                    style={{ cursor: "pointer" }}
                    rel="noopener noreferrer"
                    className="link"
                  >
                    Read the full Balance guide
                  </a>
                </Typography.Link>
              </Typography.Text>
            }
          />
          <VideoPlayerModal
            isOpened={isTutorialVisible}
            close={() => setTutorialVisible(false)}
            videoUrl="https://www.youtube.com/watch?v=fY_g9TTJ7Io"
          />
          <TitleSection
            icon="card"
            stacked={showFullTitle}
            title={balanceTitle}
            description={
              isAdminViewingUser
                ? userInfoForAdmin?.email
                : "All information about your balance and payment methods."
            }
            style={{ margin: isMobile ? "-16px -16px 16px" : "-32px -32px 24px" }}
          >
            {!isAdminViewingUser && (
              <>
                <a className="title-section-tutorial-link" onClick={() => setOpenFaq(true)}>
                  <Icon type="info_bubble" />
                  {t("Learn more about Balance")}
                </a>
                <a className="title-section-tutorial-link" onClick={() => setTutorialVisible(true)}>
                  <Icon type="watch_tutorial" />
                  {t("Watch tutorial")}
                </a>
              </>
            )}
          </TitleSection>
        </>
      )}

      {withTitleBlock && showAccountBalanceNotice && (
        <Col span={24} style={{ marginBottom: 24 }}>
          <Alert
            type="warning"
            className="fullscreen-alert account-context-alert"
            showIcon
            icon={<Icon type="info_bubble" />}
            message={
              <Text>
                {t("Balance and payment methods are related to the account you are currently on.")}
              </Text>
            }
          />
        </Col>
      )}

      <CurrentBalance addFundsRef={addFundsRef} scaleRef={scaleRef} isMobile={isMobile} />
      <Divider style={{ visibility: isMobile && "hidden", margin: isMobile && "12px 0" }} />
      {!isMobile && <AutomaticTopUpBalance topUpRef={topUpRef} isMobile={isMobile} />}
      {balance?.activated && balance?.enable_to_topup && (
        <>
          <Divider style={{ visibility: isMobile && "hidden", margin: isMobile && "12px 0" }} />
          <PaymentMethods
            isShopifyOnlyUser={isShopifyOnlyUser}
            paymentMethodsRef={paymentMethodsRef}
            isMobile={isMobile}
          />
        </>
      )}
      {isMobile && (
        <div style={{ marginTop: 40 }}>
          <AutomaticTopUpBalance topUpRef={topUpRef} isMobile={isMobile} />
        </div>
      )}
      {!isAdmin && (
        <TourComponent
          open={demoTourStep === 7 && balanceEnabled}
          scrollIntoViewOptions
          steps={steps}
          onClose={() => dispatch(UserActions.setDemoTourStep(null))}
        />
      )}
    </div>
  )
}

export default Balance
